import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Mail, Clock, Send, Facebook, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { CONTACT, SERVICE_GROUPS } from "@/data/site";
import ReassuranceLine from "@/components/ReassuranceLine";
import { FADE_INITIAL, FADE_IN_VIEW, VIEWPORT_ONCE } from "@/lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_SERVICES = SERVICE_GROUPS.flatMap((g) => g.services.map((s) => s.title));

function XIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

export default function Contact() {
  const [searchParams] = useSearchParams();
  const preselectedService = searchParams.get("service") || "";
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: ALL_SERVICES.includes(preselectedService) ? preselectedService : "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (preselectedService && ALL_SERVICES.includes(preselectedService)) {
      setForm((f) => ({ ...f, service: preselectedService }));
    }
  }, [preselectedService]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and a message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent", {
        description: "Thank you — we'll be in touch shortly. Most documents are delivered within 24 hours.",
      });
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us at " + CONTACT.phone + ".");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-sm text-[#1E293B] outline-none transition-colors duration-200 placeholder:text-slate-400 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/25";

  return (
    <div className="bg-white">
      <section className="bg-[#0B1B3D]">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <span className="section-label text-[#D4AF37]">Contact</span>
          <h1 className="mt-4 max-w-3xl font-serif text-4xl font-bold tracking-tight text-white sm:text-5xl">
            Let's get your document prepared.
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-slate-300">
            Send us a message and we'll respond promptly. 24-hour delivery on most documents.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="grid gap-14 lg:grid-cols-[1fr_1.2fr]">
          {/* Contact info */}
          <div>
            <ReassuranceLine className="mb-8" testId="contact-reassurance" />
            <h2 className="font-serif text-2xl font-bold text-[#0B1B3D]">Reach us directly</h2>
            <div className="mt-8 space-y-5">
              <a
                href={CONTACT.phoneHref}
                data-testid="contact-phone"
                className="group flex items-center gap-4 rounded-lg border border-slate-200 p-5 transition-colors duration-200 hover:border-[#D4AF37]"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-md bg-[#0B1B3D]">
                  <Phone className="h-5 w-5 text-[#D4AF37]" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-wide text-slate-400">Phone</span>
                  <span className="text-lg font-semibold text-[#0B1B3D]">{CONTACT.phone}</span>
                </span>
              </a>
              <a
                href={CONTACT.emailHref}
                data-testid="contact-email"
                className="group flex items-center gap-4 rounded-lg border border-slate-200 p-5 transition-colors duration-200 hover:border-[#D4AF37]"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-md bg-[#0B1B3D]">
                  <Mail className="h-5 w-5 text-[#D4AF37]" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-wide text-slate-400">Email</span>
                  <span className="text-lg font-semibold text-[#0B1B3D]">{CONTACT.email}</span>
                </span>
              </a>
              <div className="flex items-center gap-4 rounded-lg border border-slate-200 p-5">
                <span className="flex h-12 w-12 items-center justify-center rounded-md bg-[#0B1B3D]">
                  <Clock className="h-5 w-5 text-[#D4AF37]" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-wide text-slate-400">Turnaround</span>
                  <span className="text-lg font-semibold text-[#0B1B3D]">24 Hour Delivery</span>
                </span>
              </div>
            </div>

            <div className="mt-8 flex items-center gap-3">
              <a
                href={CONTACT.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-300 text-[#0B1B3D] transition-colors duration-200 hover:border-[#D4AF37] hover:text-[#D4AF37]"
              >
                <Facebook className="h-4 w-4" />
              </a>
              <a
                href={CONTACT.x}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X (Twitter)"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-300 text-[#0B1B3D] transition-colors duration-200 hover:border-[#D4AF37] hover:text-[#D4AF37]"
              >
                <XIcon className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>

          {/* Form */}
          <motion.form
            onSubmit={submit}
            data-testid="contact-form"
            initial={FADE_INITIAL}
            whileInView={FADE_IN_VIEW}
            viewport={VIEWPORT_ONCE}
            transition={{ duration: 0.5 }}
            className="rounded-lg border border-slate-200 bg-[#F8FAFC] p-8 sm:p-10"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <label className="mb-1.5 block text-sm font-medium text-[#0B1B3D]">Full Name *</label>
                <input
                  data-testid="contact-name-input"
                  value={form.name}
                  onChange={update("name")}
                  className={inputClass}
                  placeholder="Jane Doe"
                />
              </div>
              <div className="sm:col-span-1">
                <label className="mb-1.5 block text-sm font-medium text-[#0B1B3D]">Email *</label>
                <input
                  data-testid="contact-email-input"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  className={inputClass}
                  placeholder="you@example.com"
                />
              </div>
              <div className="sm:col-span-1">
                <label className="mb-1.5 block text-sm font-medium text-[#0B1B3D]">Phone</label>
                <input
                  data-testid="contact-phone-input"
                  value={form.phone}
                  onChange={update("phone")}
                  className={inputClass}
                  placeholder="(555) 555-5555"
                />
              </div>
              <div className="sm:col-span-1">
                <label className="mb-1.5 block text-sm font-medium text-[#0B1B3D]">Service of Interest</label>
                <select
                  data-testid="contact-service-select"
                  value={form.service}
                  onChange={update("service")}
                  className={inputClass}
                >
                  <option value="">Select a service (optional)</option>
                  {ALL_SERVICES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-sm font-medium text-[#0B1B3D]">Message *</label>
                <textarea
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={update("message")}
                  rows={5}
                  className={inputClass + " resize-none"}
                  placeholder="Tell us what you need prepared and any relevant details."
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-button"
              className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#0B1B3D] px-7 py-3.5 font-semibold text-white transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-[#14284f] disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  Send Message <Send className="h-4 w-4" />
                </>
              )}
            </button>
            <p className="mt-4 text-xs text-slate-500">
              By submitting, you acknowledge that Draft &amp; Defend is not a law firm and does not
              provide legal advice.
            </p>
          </motion.form>
        </div>
      </section>
    </div>
  );
}
